import { GetStaticPropsContext } from 'next'
import { withStaticPropsMocks } from '../playwright/utils/withMockingProps'
import withError from '../components/withError'
import { getLocationsWithChildren } from '../api/locations'
import { HomePageProps } from '../types'
import HomePage from '../components/HomePage'
import { getSpecialtyBySlug } from '../api/specialties'
import { getSearchAutocomplete, getSearchKeywords } from '../api/autocomplete'
import returnNextErrorPage from '../utils/returnNextErrorPage'
import extractAllChildrenFromLocationsTree from '../utils/extractAllChildrenFromLocationsTree'
import getLanguageFromLocale from '../utils/getLanguageFromLocale'
import { getEndorsements, getKeywords, getLocations, getReviews, getSpecialties, getVideoReviews } from 'api/homePage'
import useHomePageReviews from 'hooks/useHomePageReviews'
import useHomePageEndorsements from 'hooks/useHomePageEndorsements'
import useHomePageSpecialties from 'hooks/useHomePageSpecialties'
import useVideoReviews from 'hooks/useVideoReviews'
import { TENANTS } from '../constants/global'
import { AutocompleteSearchTypes } from '../providers/HomePage/types'
import { getHermesSettings } from '@hermes/settings'
import getConfigVariable from '../utils/getConfigVariable'

const stage = getConfigVariable('STAGE')

export const getStaticProps = withStaticPropsMocks(async (context: GetStaticPropsContext) => {
  const locale = context.locale as string

  const tenantSettings = await getHermesSettings({
    type: 'tenant',
    langTenantUrl: locale,
    stage
  })

  try {
    if (locale === TENANTS.AR_UAE || locale === TENANTS.WPA) {
      return {
        notFound: true
      }
    }

    const language = getLanguageFromLocale(locale)
    const locationsResponsePromise = getLocationsWithChildren(locale)
    const customSpecialtiesSection = locale === TENANTS.DE

    const [
      reviewsResponse,
      endorsementsResponse,
      specialtiesResponse,
      videoReviewsResponse,
      locationsForViewResponse,
      hospitalResponse,
      keywordsForViewResponse,
      locationsResponse,
      specialtyResponse,
      searchAutocomplete
    ] = await Promise.all([
      getReviews({ lang: locale }),
      getEndorsements({ lang: locale }),
      getSpecialties({ lang: locale, limit: customSpecialtiesSection ? 11 : 6 }),
      getVideoReviews({ lang: locale }),
      getLocations({ lang: locale }),
      getLocations({ lang: locale, type: 'hospital' }),
      getKeywords({ lang: locale }),
      locationsResponsePromise,
      getSpecialtyBySlug({
        language,
        locale,
        slug: 'all'
      }),
      getSearchAutocomplete({
        searchString: '',
        lang: locale,
        // check if it could be different per tenant
        searchType: AutocompleteSearchTypes.specialist
      })
    ])

    const defaultLocations = extractAllChildrenFromLocationsTree(locationsResponse)

    const locationsAutocomplete = tenantSettings.defaultLocations.map((location: Record<string, string>) => ({
      ...location,
      place_id: location.placeId,
      name: { [language]: location.name }
    }))

    const specialistsAutocomplete = searchAutocomplete.specialists || []
    const practicesAutocomplete = searchAutocomplete.practices || []

    if (!specialtyResponse) {
      return returnNextErrorPage({ statusCode: 404 })
    }

    const defaultSearchKeywords = await getSearchKeywords({
      locale,
      keywordId: specialtyResponse.id,
      keywordType: specialtyResponse.type,
      locationId: defaultLocations[0]?.id
    })

    const { transformedReviews } = useHomePageReviews({ reviews: reviewsResponse, lang: language })

    const { transformedEndorsements } = useHomePageEndorsements({ endorsements: endorsementsResponse, lang: language })

    const { transformedSpecialties } = useHomePageSpecialties({
      specialties: specialtiesResponse,
      customSpecialtiesSection
    })

    const { transformedReviews: transformedVideoReviews } = useVideoReviews({
      videoReviews: videoReviewsResponse,
      lang: language
    })

    return {
      props: {
        ...defaultSearchKeywords,
        defaultLocation: defaultLocations[0],
        locationsAutocomplete,
        specialistsAutocomplete,
        practicesAutocomplete,
        reviews: transformedReviews,
        endorsements: transformedEndorsements,
        popularSpecialties: transformedSpecialties,
        videoReviews: transformedVideoReviews,
        locationsForView: locationsForViewResponse,
        hospitalsForView: hospitalResponse,
        keywordsForView: keywordsForViewResponse,
        customSpecialtiesSection
      },
      revalidate: 60 * 60 * 24 * 7 // 7 days
    }
  } catch (e) {
    console.error('error', e)

    return returnNextErrorPage({ statusCode: 500 })
  }
}, '')

const HomePageWitError = withError<HomePageProps>(HomePage)

export default HomePageWitError
